import {useCallback, useMemo} from 'react';

import {useUnit} from 'effector-react';
import {useTranslation} from 'react-i18next';

import {METABASE_URL} from '@app/env/metabase';
import {ROUTES} from '@app/routing/routes';

import {userModel} from '@entities/user/model';
import {UserRole} from '@entities/user/types';
import {AsideItemData} from '@shared/ui/Layout/default/types';

interface AsideItemConfig extends AsideItemData {
    label: string;
    roles?: readonly UserRole[];
}

export const ALL_ROLES: readonly UserRole[] = [
    'DIRECTOR',
    'EMPLOYEE',
    'CHIEF_INSPECTOR',
    'INSPECTOR',
    'OPERATOR',
    'DDA_ADMINISTRATOR',
];
export const TAXPAYER_ROLES: readonly UserRole[] = ['DIRECTOR', 'EMPLOYEE'];
export const MANAGEMENT_ROLES: readonly UserRole[] = ['INSPECTOR', 'CHIEF_INSPECTOR', 'OPERATOR'];
export const CREATOR_ROLES: readonly UserRole[] = ['DIRECTOR', 'CHIEF_INSPECTOR', 'OPERATOR'];
export const INSPECTOR_ROLES: readonly UserRole[] = ['CHIEF_INSPECTOR', 'INSPECTOR'];
export const ADMIN_ROLES: readonly UserRole[] = ['DDA_ADMINISTRATOR'];
export const NOT_DDA_ADMIN_ROLES: readonly UserRole[] = ALL_ROLES.filter(
    (r) => r !== 'DDA_ADMINISTRATOR',
);

const ROUTE_ACCESS_CONFIG: Record<string, readonly UserRole[]> = {
    [ROUTES.PROFILE]: ALL_ROLES.filter((r) => r !== 'DDA_ADMINISTRATOR'),
    [ROUTES.DASHBOARD]: TAXPAYER_ROLES,
    [ROUTES.OFFICER_SUMMARY]: MANAGEMENT_ROLES,
    [ROUTES.TAXPAYERS]: MANAGEMENT_ROLES,
    [ROUTES.TAXPAYER_DETAILS]: MANAGEMENT_ROLES,
    [ROUTES.INVOICES]: NOT_DDA_ADMIN_ROLES,
    [ROUTES.INVOICE_DETAILS]: ALL_ROLES,
    [ROUTES.RELATIONS]: MANAGEMENT_ROLES,
    [ROUTES.COUNTERPARTIES]: TAXPAYER_ROLES,
    [ROUTES.SUBSCRIPTIONS]: TAXPAYER_ROLES,

    [ROUTES.EMPLOYERS_CONTROL]: CREATOR_ROLES,

    [ROUTES.AUDIT]: ADMIN_ROLES,
    [ROUTES.PREFERENCES]: ADMIN_ROLES,
};

const ROUTE_ACCESS_KEYS = Object.keys(ROUTE_ACCESS_CONFIG).sort((a, b) => b.length - a.length);

const getRouteAccessRoles = (path: string) => {
    const matchedRoutePrefix = ROUTE_ACCESS_KEYS.find(
        (routePrefix) => path === routePrefix || path.startsWith(`${routePrefix}/`),
    );

    return matchedRoutePrefix ? ROUTE_ACCESS_CONFIG[matchedRoutePrefix] : undefined;
};

const ASIDE_ITEMS_CONFIG: AsideItemConfig[] = [
    {
        label: 'menu.summary',
        to: ROUTES.DASHBOARD,
        icon: 'summaryTab',
    },
    {
        label: 'menu.summary',
        to: ROUTES.OFFICER_SUMMARY,
        icon: 'summaryTab',
    },
    {
        label: 'menu.taxpayers',
        to: ROUTES.TAXPAYERS,
        icon: 'counterpartiesTab',
    },
    {
        label: 'menu.relations',
        to: ROUTES.RELATIONS,
        icon: 'balanceTab',
    },
    ...(METABASE_URL
        ? [
              {
                  label: 'menu.reports',
                  to: METABASE_URL,
                  icon: 'auditTab' as const,
                  external: true,
                  roles: MANAGEMENT_ROLES,
              },
          ]
        : []),
    {
        label: 'menu.invoices',
        to: ROUTES.INVOICES,
        icon: 'invoicesTab',
    },
    {
        label: 'menu.counterparties',
        to: ROUTES.COUNTERPARTIES,
        icon: 'counterpartiesTab',
    },
    {
        label: 'menu.employersControl',
        to: ROUTES.EMPLOYERS_CONTROL,
        icon: 'usersTab',
    },
    {
        label: 'menu.audit',
        to: ROUTES.AUDIT,
        icon: 'auditTab',
    },
    {
        label: 'menu.preferences',
        to: ROUTES.PREFERENCES,
        icon: 'options',
    },
];

export const hasUserRouteAccess = (
    role: UserRole | null,
    path?: string | readonly string[],
): boolean => {
    if (!path) return true;
    if (typeof path !== 'string') return path.some((item) => hasUserRouteAccess(role, item));

    const allowedRoles = getRouteAccessRoles(path);
    return !allowedRoles?.length || (Boolean(role) && allowedRoles.includes(role as UserRole));
};

export const getUserDefaultPrivateRoute = (role: UserRole | null) => {
    if (hasUserRouteAccess(role, ROUTES.DASHBOARD)) return ROUTES.DASHBOARD;
    if (hasUserRouteAccess(role, ROUTES.OFFICER_SUMMARY)) return ROUTES.OFFICER_SUMMARY;
    if (hasUserRouteAccess(role, ROUTES.PROFILE)) return ROUTES.PROFILE;
    if (hasUserRouteAccess(role, ROUTES.AUDIT)) return ROUTES.AUDIT;
    return ROUTES.LOGIN;
};

export const useUserPermissions = () => {
    const {t} = useTranslation();
    const role = useUnit(userModel.role);

    const hasPathAccess = useCallback(
        (path?: string | readonly string[]) => hasUserRouteAccess(role, path),
        [role],
    );

    const asideItems = useMemo(
        () =>
            ASIDE_ITEMS_CONFIG.filter((item) =>
                item.external
                    ? Boolean(role && item.roles?.includes(role))
                    : hasPathAccess(item.to),
            ).map(({label, to, icon, external}) => ({
                to,
                icon,
                external,
                label: t(label),
            })),
        [hasPathAccess, role, t],
    );

    return {
        asideItems,
        defaultPrivateRoute: getUserDefaultPrivateRoute(role),
        profilePath: hasPathAccess(ROUTES.PROFILE) ? ROUTES.PROFILE : undefined,
        hasPathAccess,
    };
};
